import React, { useState } from 'react';
import { Moon, Sun, Palette, Calendar, EyeOff } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import {
  DateFormatPreference,
  getDateFormatPreference,
  setDateFormatPreference,
  previewFormat,
} from '../utils/dateFormat';
import { MfaSettingsSection } from '../components/settings/MfaSettingsSection';
import { NotificationPreferencesSection } from '../components/settings/NotificationPreferencesSection';
import { DangerZoneSection } from '../components/settings/DangerZoneSection';
import { useRedactionMode } from '../contexts/RedactionModeContext';

const DATE_FORMAT_OPTIONS: { value: DateFormatPreference; label: string }[] = [
  { value: 'dd/MM/yyyy', label: 'DD/MM/YYYY' },
  { value: 'dd MMM yyyy', label: 'DD Mon YYYY' },
  { value: 'yyyy-MM-dd', label: 'YYYY-MM-DD' },
];

export const SettingsPage: React.FC = () => {
  const { theme, setTheme } = useTheme();
  const { isEnabled: redactionEnabled, toggle: toggleRedaction } = useRedactionMode();
  const [dateFormat, setDateFormat] = useState<DateFormatPreference>(getDateFormatPreference);
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

  const handleDateFormatChange = (format: DateFormatPreference) => {
    setDateFormat(format);
    setDateFormatPreference(format);
  };

  return (
    <div className="p-6 max-w-3xl mx-auto dark:bg-medical-900">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-medical-900 dark:text-medical-100">Settings</h1>
        <p className="text-medical-600 dark:text-medical-400 mt-1">
          Preferences are saved automatically.
        </p>
      </div>

      <div className="space-y-6">
        {/* Theme */}
        <div className="card dark:bg-medical-800 dark:border-medical-700">
          <h3 className="text-lg font-medium text-medical-900 dark:text-medical-100 mb-4 flex items-center space-x-2">
            <Palette className="h-5 w-5" />
            <span>Theme</span>
          </h3>

          <div className="flex space-x-3">
            {[
              { value: 'light' as const, icon: Sun, label: 'Light' },
              { value: 'dark' as const, icon: Moon, label: 'Dark' },
            ].map(({ value, icon: Icon, label }) => (
              <button
                key={value}
                onClick={() => setTheme(value)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md border transition-all ${
                  theme === value
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 shadow-sm'
                    : 'border-medical-300 dark:border-medical-600 hover:bg-medical-50 dark:hover:bg-medical-700/50 text-medical-700 dark:text-medical-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Date Format */}
        <div className="card dark:bg-medical-800 dark:border-medical-700">
          <h3 className="text-lg font-medium text-medical-900 dark:text-medical-100 mb-4 flex items-center space-x-2">
            <Calendar className="h-5 w-5" />
            <span>Date Format</span>
          </h3>

          <div className="space-y-2">
            {DATE_FORMAT_OPTIONS.map(({ value, label }) => (
              <button
                key={value}
                onClick={() => handleDateFormatChange(value)}
                className={`w-full text-left px-4 py-3 rounded-md border transition-all flex items-center justify-between ${
                  dateFormat === value
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 shadow-sm'
                    : 'border-medical-300 dark:border-medical-600 hover:bg-medical-50 dark:hover:bg-medical-700/50 text-medical-700 dark:text-medical-300'
                }`}
              >
                <span className="text-sm font-medium">{label}</span>
                <span className="text-sm text-medical-500 dark:text-medical-400 font-mono">
                  {previewFormat(value)}
                </span>
              </button>
            ))}
          </div>
          <p className="text-xs text-medical-500 dark:text-medical-400 mt-3">
            Controls how dates appear across the application.
          </p>
        </div>

        {/* Redaction Mode */}
        <div className="card dark:bg-medical-800 dark:border-medical-700">
          <h3 className="text-lg font-medium text-medical-900 dark:text-medical-100 mb-4 flex items-center space-x-2">
            <EyeOff className="h-5 w-5" />
            <span>Redaction Mode</span>
          </h3>

          <div className="flex items-center justify-between">
            <div className="flex-1 mr-4">
              <p className="text-sm text-medical-700 dark:text-medical-300">
                Blur patient names, dates of birth, and other personal information to protect against shoulder surfing.
              </p>
              <p className="text-xs text-medical-500 dark:text-medical-400 mt-1">
                Hover over blurred content to reveal it temporarily.
              </p>
            </div>
            <button
              onClick={toggleRedaction}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                redactionEnabled ? 'bg-amber-500' : 'bg-medical-300 dark:bg-medical-600'
              }`}
              role="switch"
              aria-checked={redactionEnabled}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  redactionEnabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          <p className="text-xs text-medical-500 dark:text-medical-400 mt-3">
            Keyboard shortcut: {isMac ? '\u2318' : 'Ctrl'}+Shift+P
          </p>
        </div>

        {/* Notification Preferences */}
        <NotificationPreferencesSection />

        {/* Two-Factor Authentication */}
        <MfaSettingsSection />

        {/* Danger Zone */}
        <DangerZoneSection />
      </div>
    </div>
  );
};
